import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { TotauxFacture } from '../ventes/TotauxFacture.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { Badge, Button, classesBouton, Price } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { ArrowLeft, Download, Lock } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { archiveFacturesApi, useArchiveFacture } from '../../api/archiveFactures.js';
import type { CartelLigneFacture } from '../../api/ventes.js';
import { enregistrerFichier, ouvrirUrlSignee } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { teinteDeLEtatFacture } from '../ventes/etatUi.js';
import styles from './ArchiveFactures.module.css';

/**
 * Fiche d'une pièce archivée (D21) : une facture ou un avoir ÉMIS, en LECTURE
 * SEULE — lignes, totaux, règlements déjà enregistrés, PDF et XML Factur-X.
 * Aucune action : on ne règle, n'annule ni ne corrige rien ici.
 */
export function ArchiveFacturePage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'ventes', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { galerieSlug, galerie } = useShellContext();
  const { id } = useParams<{ id: string }>();
  const fiche = useArchiveFacture(galerieSlug, id ?? null);
  const [erreurPdf, setErreurPdf] = useState<string | null>(null);

  const retour = (
    <Link className={styles.retour} to={`/${galerieSlug}/archive-factures`}>
      <ArrowLeft size={16} strokeWidth={1.5} aria-hidden />
      {t('regie:archiveFactures.titre')}
    </Link>
  );

  if (fiche.isLoading) {
    return (
      <div className={styles.page}>
        {retour}
        <p className={styles.vide}>{t('commun:chargement')}</p>
      </div>
    );
  }
  if (fiche.isError || !fiche.data) {
    return (
      <div className={styles.page}>
        {retour}
        <p className={styles.vide} role="alert">
          {estApiError(fiche.error) ? fiche.error.message : t('commun:erreurReseau')}
        </p>
      </div>
    );
  }

  const { facture, reglements, avoirs } = fiche.data;

  async function ouvrirPdf(): Promise<void> {
    setErreurPdf(null);
    try {
      const reponse = await dansLaPorteeCourante((appel) =>
        archiveFacturesApi.urlPdf(galerieSlug, facture.id, appel),
      );
      ouvrirUrlSignee(reponse.url);
    } catch (erreur) {
      setErreurPdf(estApiError(erreur) ? erreur.message : t('commun:erreurReseau'));
    }
  }

  return (
    <div className={styles.page}>
      {retour}

      <div className={styles.barre}>
        <div className={styles.documentsFiche}>
          {facture.pdfDisponible ? (
            <Button size="sm" variant="secondary" onClick={() => void ouvrirPdf()}>
              <Download size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:ventes.fiche.telechargerPdf')}
            </Button>
          ) : null}
          {facture.facturXDisponible ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void dansLaPorteeCourante((appel) =>
                  archiveFacturesApi.facturX(galerieSlug, facture.id, appel),
                ).then(
                  (fichier) => {
                    enregistrerFichier(fichier, `${facture.numero ?? facture.id}.xml`);
                  },
                  () => undefined,
                );
              }}
            >
              <Download size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:ventes.fiche.telechargerFacturX')}
            </Button>
          ) : null}
        </div>
        <span className={styles.etiquette}>
          <Lock size={12} strokeWidth={2} aria-hidden />
          {t('regie:archiveFactures.lectureSeule')}
        </span>
      </div>
      {erreurPdf ? (
        <p className={styles.erreur} role="alert">
          {erreurPdf}
        </p>
      ) : null}

      <div className={styles.corps}>
        <FeuillePiece
          emetteur={galerie.nom ?? ''}
          nature={
            facture.type === 'avoir' ? (
              <Badge tone="neutral">{t('ventes:factures.avoir')}</Badge>
            ) : (
              t('ventes:factures.facture')
            )
          }
          destinataire={{
            legende: t('regie:ventes.colonneContact'),
            nom: facture.contactNom ?? '—',
          }}
          numero={facture.numero ?? t('ventes:factures.brouillon')}
          brouillon={!facture.numero}
          tampon={
            <Badge tone={teinteDeLEtatFacture(facture.etat)}>
              {t(`ventes:factures.${facture.etat}`)}
            </Badge>
          }
          meta={
            facture.emiseLe
              ? t('ventes:factures.emise_le', {
                  date: formatDate(new Date(facture.emiseLe), langue, 'long'),
                })
              : null
          }
          etat={facture.etat}
        >
          <section className={styles.sectionLignes}>
            <h2 className={styles.legendeMasquee}>{t('regie:ventes.fiche.lignesTitre')}</h2>
            <div className={styles.lignesEnveloppe}>
              <table className={styles.lignes}>
                <thead>
                  <tr>
                    <th scope="col">{t('regie:ventes.fiche.colonneDesignation')}</th>
                    <th scope="col" className={styles.colCentre}>
                      {t('regie:ventes.fiche.colonneQuantite')}
                    </th>
                    <th scope="col" className={styles.colMontant}>
                      {t('regie:ventes.fiche.colonnePrixHt')}
                    </th>
                    <th scope="col">{t('regie:ventes.fiche.colonneRegime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {facture.lignes.map((ligne, rang) => (
                    <tr key={`${String(rang)}-${ligne.designation}`}>
                      <td className={styles.celDesignation}>
                        <Designation designation={ligne.designation} cartel={ligne.cartel} />
                      </td>
                      <td
                        className={styles.colCentre}
                        data-libelle={t('regie:ventes.fiche.colonneQuantite')}
                      >
                        {ligne.quantite}
                      </td>
                      <td
                        className={styles.colMontant}
                        data-libelle={t('regie:ventes.fiche.colonnePrixHt')}
                      >
                        <Price
                          cents={ligne.prixUnitaireNetCentimes}
                          lang={langue}
                          currency={facture.devise}
                        />
                      </td>
                      <td
                        className={styles.celRegime}
                        data-libelle={t('regie:ventes.fiche.colonneRegime')}
                      >
                        {t(`ventes:regimes.${ligne.regimeTva}`)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.sectionTotaux}>
            <h2 className={styles.legendeMasquee}>{t('regie:ventes.fiche.totauxTitre')}</h2>
            <TotauxFacture facture={facture} langue={langue} />
          </section>
        </FeuillePiece>

        <div className={styles.colonne}>
          <section className={styles.carte}>
            <h2 className={styles.carteTitre}>{t('ventes:reglements.titre')}</h2>
            {reglements.length === 0 ? (
              <p className={styles.aide}>{t('regie:ventes.fiche.aucunReglement')}</p>
            ) : (
              <ul className={styles.liste}>
                {reglements.map((reglement) => (
                  <li key={reglement.id}>
                    <span className={styles.listeDate}>
                      {formatDate(new Date(reglement.date), langue, 'court')}
                    </span>
                    <span className={styles.listeMode}>
                      {t(`ventes:reglements.${reglement.mode}`)}
                    </span>
                    <Price
                      cents={reglement.montantCentimes}
                      lang={langue}
                      currency={reglement.devise}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {avoirs.length > 0 ? (
            <section className={styles.carte}>
              <h2 className={styles.carteTitre}>{t('regie:ventes.fiche.avoirsTitre')}</h2>
              <ul className={styles.liste}>
                {avoirs.map((avoir) => (
                  <li key={avoir.id}>
                    <Link
                      className={styles.lienPiece}
                      to={`/${galerieSlug}/archive-factures/${avoir.id}`}
                    >
                      {avoir.numero ?? '—'}
                    </Link>
                    <Price cents={avoir.totalTtcCentimes} lang={langue} currency={facture.devise} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {facture.factureOrigineId ? (
            <Link
              className={classesBouton('secondary', 'sm')}
              to={`/${galerieSlug}/archive-factures/${facture.factureOrigineId}`}
            >
              {t('regie:ventes.fiche.voirFactureOrigine')}
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Désignation d'une ligne archivée : le cartel figé s'il existe, sinon le libellé. */
function Designation({
  designation,
  cartel,
}: {
  designation: string;
  cartel: CartelLigneFacture | null | undefined;
}): JSX.Element {
  if (!cartel || (!cartel.artiste && !cartel.titre)) return <span>{designation}</span>;
  return (
    <ObjetCartel
      titre={cartel.titre ?? designation}
      artiste={cartel.artiste}
      annee={cartel.annee}
      precisions={[cartel.technique, cartel.dimensions, cartel.edition]}
      numeroInventaire={cartel.numeroInventaire}
    />
  );
}
